import { useState } from 'react';
import { ActionButton } from '../concept/action-button';
import { useToast } from '../primitives/use-toast';
// Types only: design-system/ value-imports nothing from app code. The human
// copy for each refusal arrives as the thrown error's message.
import type { SendLinkKind, SendLinkResult } from '../../lib/dashboard/sendLink';

/**
 * "Email to Anna", for one of the two links a coach hands a client (SF-91).
 *
 * No confirmation step: the recipient line under the button is the
 * confirmation, and the server only ever sends to that stored address. The
 * server builds the link itself, so the scan link does not have to be created
 * in the browser first.
 *
 * `before` is the action this one sits beside — "Create scan link", the share
 * trigger — so the two read as one row and wrap together on a phone. It is
 * rendered through here even while `available` is false, so turning sharing on
 * does not move the share trigger in the tree and remount its open dialog.
 */
export const EmailLinkAction: React.FC<{
  kind: SendLinkKind;
  clientName: string;
  clientEmail?: string;
  onSend: (kind: SendLinkKind) => Promise<SendLinkResult>;
  /** When the profile says this link was last emailed, if it says. */
  lastSentAt?: string;
  before?: React.ReactNode;
  /** False hides the action, keeping `before` where it is. */
  available?: boolean;
}> = ({ kind, clientName, clientEmail, onSend, lastSentAt, before, available = true }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [sentAt, setSentAt] = useState<string | undefined>(lastSentAt);
  const shownAt = latest(sentAt, lastSentAt);
  const email = clientEmail?.trim();

  const send = async () => {
    setBusy(true);
    try {
      const result = await onSend(kind);
      setSentAt(result.sentAt ?? new Date().toISOString());
      toast({ title: `Email sent to ${email ? shortEmail(email) : result.to}` });
    } catch (e) {
      toast({
        title:
          e instanceof Error && e.message
            ? e.message
            : "Couldn't send the email. Try again or copy the link.",
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-0.5 flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        {before}
        {available && email && (
          <ActionButton
            className="max-sm:min-h-11"
            disabled={busy}
            onClick={send}
            testId={`email-${kind}-link`}
          >
            {busy ? 'Sending…' : `Email to ${clientName}`}
          </ActionButton>
        )}
      </div>
      {/* One line, so "Sent" stays with the address it went to rather than
          wrapping onto a line of its own in the narrow rail column. */}
      {available && (
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          <span data-testid={`email-${kind}-to`} className="break-all">
            {email ? `to ${email}` : 'No email on file — add one in Settings to send it.'}
          </span>
          {email && shownAt && (
            <>
              <span aria-hidden="true"> · </span>
              <span data-testid={`email-${kind}-sent`} className="whitespace-nowrap tabular-nums">
                Sent {sentTime(shownAt)}
              </span>
            </>
          )}
        </p>
      )}
    </div>
  );
};

const latest = (a?: string, b?: string) => {
  if (!a) return b;
  if (!b) return a;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
};

/** "anna@example.com" → "anna@…", for a toast that should not shout the address. */
const shortEmail = (address: string) => {
  const at = address.indexOf('@');
  return at > 0 ? `${address.slice(0, at)}@…` : address;
};

/** Local wall-clock time, "14:02". */
const sentTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
